import { createContext, useContext } from "react";

interface ReviewSwipeScrollLockValue {
  isScrollLocked: boolean;
  setScrollLocked: (locked: boolean) => void;
}

/**
 * Whether a rating swipe currently owns the gesture, and so the card's own scroll views must hold
 * still.
 *
 * Without this, a drag that starts as a scroll and turns into a leftward swipe leaves the content
 * scrolling under the panel while the panel slides away: the JS pan responder wins the gesture, but
 * the native scroll view keeps the momentum it had already built, so the card lands rated AND
 * scrolled somewhere the user never chose.
 *
 * Defaults to unlocked so a panel rendered outside a review (browse, search, a deck preview) scrolls
 * normally with no provider in the tree.
 */
export const ReviewSwipeScrollLockContext = createContext<ReviewSwipeScrollLockValue>({
  isScrollLocked: false,
  setScrollLocked: () => {},
});

/** Read by scroll views inside a card: `scrollEnabled={!useIsReviewSwipeScrollLocked()}`. */
export function useIsReviewSwipeScrollLocked(): boolean {
  return useContext(ReviewSwipeScrollLockContext).isScrollLocked;
}

/** Used by a swipe responder to hold every scroll view in the card still for the gesture. */
export function useSetReviewSwipeScrollLocked(): (locked: boolean) => void {
  return useContext(ReviewSwipeScrollLockContext).setScrollLocked;
}
